import React, { forwardRef, useId, useImperativeHandle, useRef } from "react";
import type {
  MassCredentialEmailBlock,
  MassCredentialEmailEdits,
} from "./massCredentialEmailOverrides";

export type MassCredentialEmailEditorHandle = {
  /** Reads the current editable text from the page. */
  readEdits: () => MassCredentialEmailEdits;
};

export type MassCredentialEmailEditorProps = {
  chamberName: string;
  /** The values shown when editing starts. */
  edits: MassCredentialEmailEdits;
  /** Changing this value restores `edits` in the editable regions. */
  resetKey: number;
};

// Design: CMP Dashboard.dc.html massCredEditOutline.
const editableOutline = "1.5px dashed #E1A731";

const blockLevelTags = new Set(["DIV", "P", "SECTION", "ARTICLE"]);

const ignoredTags = new Set(["SCRIPT", "STYLE", "TEMPLATE"]);

const readInlineText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (node.nodeName === "BR") return "\n";
  if (ignoredTags.has(node.nodeName)) return "";
  return Array.from(node.childNodes).map(readInlineText).join("");
};

const hasBlockChild = (element: Element) =>
  Array.from(element.children).some(
    (child) => blockLevelTags.has(child.tagName) || child.tagName === "UL" || child.tagName === "OL",
  );

// contentEditable can nest divs or split lists when staff press Enter.
// Read the structure into plain text blocks; never keep pasted markup.
export const readMassCredentialEmailBlocks = (root: Element): MassCredentialEmailBlock[] => {
  const blocks: MassCredentialEmailBlock[] = [];
  let pendingText = "";
  const flushText = () => {
    if (pendingText.trim()) blocks.push({ kind: "paragraph", text: pendingText.trim() });
    pendingText = "";
  };
  Array.from(root.childNodes).forEach((node) => {
    if (node instanceof Element && (node.tagName === "UL" || node.tagName === "OL")) {
      flushText();
      blocks.push({
        kind: "list",
        items: Array.from(node.querySelectorAll("li")).map((item) => readInlineText(item).trim()),
      });
      return;
    }
    if (node instanceof Element && blockLevelTags.has(node.tagName)) {
      flushText();
      if (hasBlockChild(node)) blocks.push(...readMassCredentialEmailBlocks(node));
      else blocks.push({ kind: "paragraph", text: readInlineText(node).trim() });
      return;
    }
    pendingText += readInlineText(node);
  });
  flushText();
  return blocks;
};

const readSingleLine = (element: HTMLElement | null) =>
  (element?.textContent || "").replace(/\s+/g, " ").trim();

const pastePlainText = (event: React.ClipboardEvent<HTMLElement>) => {
  event.preventDefault();
  const text = event.clipboardData.getData("text/plain");
  if (typeof document.execCommand === "function") document.execCommand("insertText", false, text);
};

const preventLineBreak = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key === "Enter") event.preventDefault();
};

const renderBlocks = (blocks: MassCredentialEmailBlock[]) =>
  blocks.map((block, index) =>
    block.kind === "list" ? (
      <ul key={index} className="m-0 flex list-disc flex-col gap-[6px] pl-[22px]">
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{item}</li>
        ))}
      </ul>
    ) : (
      <div key={index} className="whitespace-pre-line">{block.text}</div>
    ),
  );

const editableBlockProps = (label: string) => ({
  contentEditable: true,
  suppressContentEditableWarning: true,
  role: "textbox",
  "aria-multiline": true,
  "aria-label": label,
  onPaste: pastePlainText,
  className: "flex flex-col gap-[12px] rounded-[6px]",
  style: { outline: editableOutline, outlineOffset: 4 },
});

const editableLineProps = (label: string) => ({
  contentEditable: true,
  suppressContentEditableWarning: true,
  role: "textbox",
  "aria-label": label,
  onPaste: pastePlainText,
  onKeyDown: preventLineBreak,
  className: "rounded-[4px]",
  style: { outline: editableOutline, outlineOffset: 2 },
});

/**
 * The editable Member Dashboard welcome email, laid out as the design's
 * preview. Only the introduction, the closing and the signature are editable.
 */
const MassCredentialEmailEditor = forwardRef<
  MassCredentialEmailEditorHandle,
  MassCredentialEmailEditorProps
>(({ chamberName, edits, resetKey }, ref) => {
  const introRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef<HTMLDivElement>(null);
  const signatureNameRef = useRef<HTMLSpanElement>(null);
  const signatureEmailRef = useRef<HTMLSpanElement>(null);
  const signaturePhoneRef = useRef<HTMLSpanElement>(null);
  useImperativeHandle(ref, () => ({
    readEdits: () => ({
      intro: introRef.current ? readMassCredentialEmailBlocks(introRef.current) : edits.intro,
      closing: closingRef.current ? readMassCredentialEmailBlocks(closingRef.current) : edits.closing,
      signatureName: signatureNameRef.current ? readSingleLine(signatureNameRef.current) : edits.signatureName,
      signatureEmail: signatureEmailRef.current ? readSingleLine(signatureEmailRef.current) : edits.signatureEmail,
      signaturePhone: signaturePhoneRef.current ? readSingleLine(signaturePhoneRef.current) : edits.signaturePhone,
    }),
  }), [edits]);
  // The key remounts the editable regions, so React never reconciles
  // children that staff changed directly in the page.
  return (
    <div className="flex flex-col gap-[12px] px-[18px] py-[20px] text-[14px] leading-[1.6] text-[#0A0A0A]">
      <div>Dear <span>[Primary Contact Name]</span>,</div>
      <div key={`intro-${resetKey}`} ref={introRef} {...editableBlockProps("Email introduction")}>
        {renderBlocks(edits.intro)}
      </div>
      <div className="flex flex-col gap-[6px] rounded-[8px] border border-[#ECECEC] bg-[#F7F7F8] px-[16px] py-[14px]">
        <div><span className="font-bold">Email/Username:</span> <span>[Primary Contact Email]</span></div>
        <div><span className="font-bold">Temporary Password:</span> <span>[Temporary Password]</span></div>
      </div>
      <div key={`closing-${resetKey}`} ref={closingRef} {...editableBlockProps("Email closing")}>
        {renderBlocks(edits.closing)}
      </div>
      <div>Sincerely,</div>
      <div className="flex flex-col items-start gap-[10px]">
        <span key={`name-${resetKey}`} ref={signatureNameRef} {...editableLineProps("Signature name")}>{edits.signatureName}</span>
        <span key={`email-${resetKey}`} ref={signatureEmailRef} {...editableLineProps("Signature email")}>{edits.signatureEmail}</span>
        <span key={`phone-${resetKey}`} ref={signaturePhoneRef} {...editableLineProps("Signature phone")}>{edits.signaturePhone}</span>
        <span>{chamberName}</span>
      </div>
    </div>
  );
});

MassCredentialEmailEditor.displayName = "MassCredentialEmailEditor";

export type MassCredentialDiscardConfirmProps = {
  open: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
};

/**
 * Design "Discard Changes?" confirm. It renders inside the preview dialog,
 * as in the design, so the preview's focus trap keeps focus inside it.
 */
export const MassCredentialDiscardConfirm = ({
  open,
  onKeepEditing,
  onDiscard,
}: MassCredentialDiscardConfirmProps) => {
  const titleId = useId();
  const messageId = useId();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[4500] flex items-center justify-center p-[24px]"
      style={{ background: "rgba(10,10,10,.45)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
          onKeepEditing();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className="relative flex w-[min(420px,92vw)] flex-col items-center gap-[14px] rounded-[14px] border border-[#ECECEC] bg-white px-[28px] py-[30px] text-center shadow-[0_18px_48px_rgba(0,0,0,.18)]"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onKeepEditing}
          className="absolute right-[14px] top-[14px] flex h-[28px] w-[28px] items-center justify-center rounded-full border-0 bg-[#F21B1B] text-white"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
        <div className="flex h-[56px] w-[56px] items-center justify-center rounded-full bg-[#E1A731] text-white">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 8v5" /><path d="M12 16.5h.01" /><circle cx="12" cy="12" r="9" /></svg>
        </div>
        <h3 id={titleId} className="text-[18px] font-bold leading-[normal] text-[#0A0A0A]">Discard Changes?</h3>
        <p id={messageId} className="text-[14px] leading-[1.55] text-[#0A0A0A]">Are you sure you want to leave without saving? Any edits you made to this email will be lost.</p>
        <div className="mt-[6px] flex flex-wrap justify-center gap-[12px]">
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the confirm must take focus from the editor */}
          <button type="button" autoFocus onClick={onKeepEditing} className="rounded-[8px] border border-[#9E9E9E] bg-white px-[22px] py-[11px] text-[14px] font-semibold leading-[normal] text-[#0A0A0A] hover:bg-[#F7F7F8]">Keep Editing</button>
          <button type="button" onClick={onDiscard} className="rounded-[8px] border-0 bg-[#F21B1B] px-[22px] py-[11px] text-[14px] font-semibold leading-[normal] text-white hover:bg-[#D01414]">Yes, Discard</button>
        </div>
      </div>
    </div>
  );
};

export default MassCredentialEmailEditor;
